import * as Form from '@radix-ui/react-form'
import { message } from 'antd'
import {
  applyMeasurementSystemToFormField,
  assetOverviewPermissionsCheck,
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import { FeatureFormMode, SimpleFormField } from 'components/form/formTypes'
import { GeckoToggleForm } from 'components/form/GeckoToggleForm'
import {
  getFormFieldsForComponentType,
  getJSONFromComponentFormFields,
  getNewJSONForComponentType,
} from 'features/assetDatabase/utils/assetDBComponents'
import {
  getFormFieldsForFeatureType,
  getJSONFromFeatureFormFields,
  getNewJSONForFeatureType,
} from 'features/assetDatabase/utils/assetDBFeatures'
import { motion } from 'framer-motion'
import React, { useMemo, useRef } from 'react'
import { SubmitHandler } from 'react-hook-form'
import styled from 'styled-components'
import {
  ExternalUserFieldAccess,
  FormField,
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentModelJSON,
  GeckoComponentModelType,
  GeckoFeatureModelType,
  GeckoFeaturesJSON,
  GeckoMeasurementSystem,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  regenerateAssetModel,
  RolodexEntryJson,
  RolodexEntryPutJson,
} from 'types'
import { v4 as uuid4 } from 'uuid'

const StyledSubmitDiv = styled.div`
  padding-top: 5px;
  display: flex;
  justify-content: center;
`

const StyledButton = styled(Form.Submit)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: solid 1px grey;
  height: 30px;
  background: none;
  color: inherit;
  font-size: inherit;
  width: 45%;
  font-weight: bold;
`

const MotionButton = motion.create(StyledButton)

const SaveAndJSONPreview = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  justify-content: center;
`

export enum SubmissionEvent {
  Save,
  Preview,
}

const canEditAtLeastOneField = (
  formFields: FormField[],
  userPermissions: ReadonlySet<string> | undefined
): boolean => {
  const isGecko =
    userPermissions?.has(PERMISSION_GECKO_INTERNAL_MEMBER) ?? false
  const hasUnitEditPermission =
    (userPermissions?.has(PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR) ||
      userPermissions?.has(PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN)) ??
    false

  return formFields.some((formItem) => {
    const canEditField =
      isGecko ||
      (hasUnitEditPermission &&
        formItem.externalUserAccess === ExternalUserFieldAccess.ReadWrite)

    return canEditField
  })
}

interface EditableFeatureFormProps {
  assetModel: GeckoAssetModel
  selectedComponent: GeckoComponent
  featureType: string
  featureJSON?: GeckoFeaturesJSON | Record<string, unknown> | undefined
  featureID?: string | undefined
  formMode: FeatureFormMode
  measurementSystem: GeckoMeasurementSystem
  onSave: (editedAssetModel: RolodexEntryPutJson) => void
  isOverviewPage?: boolean
  inEditMode?: boolean
  inResolveMode?: boolean
  userPermissions: ReadonlySet<string> | undefined
  jsonSelector?: React.ReactNode
}

/**
 * This form allows for the easy modification of Asset Models, returning the edited JSON to the callback (either onSave or onPreview)
 */
export const EditableFeatureForm = ({
  measurementSystem,
  featureType,
  featureJSON,
  featureID,
  formMode,
  assetModel,
  selectedComponent,
  onSave,
  isOverviewPage = false,
  inEditMode = true,
  inResolveMode = false,
  userPermissions,
  jsonSelector,
}: EditableFeatureFormProps) => {
  const [, contextHolder] = message.useMessage()
  const formRef = useRef<HTMLFormElement>(null)

  const isFeature = Object.values(GeckoFeatureModelType).includes(
    featureType as GeckoFeatureModelType
  )
  const isComponent = Object.values(GeckoComponentModelType).includes(
    featureType as GeckoComponentModelType
  )

  const formFields = useMemo(() => {
    if (isFeature) {
      return getFormFieldsForFeatureType(
        featureJSON
          ? (featureJSON as unknown as GeckoFeaturesJSON)
          : getNewJSONForFeatureType(featureType as GeckoFeatureModelType)
      )
    } else if (isComponent) {
      return getFormFieldsForComponentType(
        featureJSON
          ? (featureJSON as unknown as GeckoComponentModelJSON)
          : getNewJSONForComponentType(
              featureType as GeckoComponentModelType,
              selectedComponent.data.name
            )
      )
    } else {
      return []
    }
  }, [
    featureJSON,
    featureType,
    isComponent,
    isFeature,
    selectedComponent.data.name,
  ])

  const fieldsAreEditable = useMemo(() => {
    return canEditAtLeastOneField(formFields, userPermissions)
  }, [formFields, userPermissions])

  const onSubmit: SubmitHandler<SimpleFormField> = (data) => {
    const convertedFormFields = createEditedObject(
      data,
      createUniqueFormFields(formFieldsByUniqueID)
    )

    let editedJSON = undefined
    if (isFeature) {
      editedJSON = getJSONFromFeatureFormFields(
        featureType as GeckoFeatureModelType,
        convertedFormFields[0].formFields
      )
    } else if (isComponent) {
      editedJSON = getJSONFromComponentFormFields(
        featureJSON
          ? (featureJSON as GeckoComponentModelJSON)
          : (getNewJSONForComponentType(
              featureType as GeckoComponentModelType,
              selectedComponent.data.name
            ) as GeckoComponentModelJSON),
        convertedFormFields[0].formFields
      )
    }

    const editedFeature = editedJSON as Record<string, unknown>

    // Shallow copy of the Asset Model JSON
    const assetModelRaw = assetModel.raw as RolodexEntryJson
    const assetModelComponents = assetModelRaw.data.components as Record<
      string,
      Record<string, unknown>
    >

    // If the feature passed in is a component, modify the assetModelComponents to either add or edit the component
    // Otherwise, overwrite the feature on that component
    if (
      Object.values(GeckoComponentModelType).includes(
        editedFeature.type as GeckoComponentModelType
      )
    ) {
      assetModelComponents[selectedComponent.id] = editedFeature as Record<
        string,
        unknown
      >
    } else {
      const assetModelComponent = assetModelComponents[
        selectedComponent.id
      ] as Record<string, Record<string, unknown>>

      const editedFeatureID =
        formMode === FeatureFormMode.Edit ? featureID : uuid4().slice(0, 6)
      assetModelComponent.features[String(editedFeatureID)] = editedFeature
    }
    const editedAssetModel = regenerateAssetModel(
      GeckoAssetModel.CreateFromJSON(assetModelRaw)
    )
    onSave(editedAssetModel)
  }
  const submit = () => {
    if (formRef && formRef.current) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      formRef.current.dispatchEvent(event)
    }
  }

  const formFieldsByUniqueID = useMemo(() => {
    return [
      {
        uniqueID: featureID ? featureID : selectedComponent.id,
        formFields: formFields,
      },
    ]
  }, [featureID, formFields, selectedComponent.id])

  return (
    <>
      {contextHolder}
      {userPermissions && (
        <GeckoToggleForm
          formFieldsByUniqueID={formFieldsByUniqueID}
          inEditMode={inEditMode}
          inResolveMode={inResolveMode}
          permissionsCheck={{
            userPermissions: userPermissions,
            permissionsChecker: assetOverviewPermissionsCheck,
          }}
          measurementApplication={{
            measurementApplication: applyMeasurementSystemToFormField,
            measurementSystem: measurementSystem,
          }}
          onSubmit={onSubmit}
          ref={formRef}
          numberOfColumns={1}
        />
      )}
      {!isOverviewPage && inEditMode && fieldsAreEditable ? (
        <SaveAndJSONPreview>
          <StyledSubmitDiv>
            <MotionButton
              id={SubmissionEvent.Save.toString()}
              onClick={() => submit()}>
              Save
            </MotionButton>
          </StyledSubmitDiv>
          {jsonSelector}
        </SaveAndJSONPreview>
      ) : null}
    </>
  )
}
